import { useCallback, useRef, useState } from "react";
import type { TaskBox, Result, Blocker } from "@/components/ui/box.schema";
import { RollRegister, type RollRegisterControls, type RollSubmitState } from "@/components/attendance/RollRegister";
import type { SessionAttendance } from "@/lib/api/attendance";
import { recorded, registerCommit, keepResult, registerText, registerPhrases, recoverRegisterCommit } from "@/lib/register-copy";
import { useRollDraft } from "./roll-recovery";
import { ApiError } from "@/lib/api/client";

/** The standalone roll is one Task; session Close mounts the same register as its part. */
export function useRollBox({
  roll,
  onSubmitted,
}: {
  roll?: SessionAttendance;
  onSubmitted: () => void;
}) {
  const controls = useRef<RollRegisterControls | null>(null);
  const sessionId = roll?.session_id ?? "unavailable";
  const keptRoll = useRollDraft(sessionId, roll?.roll_updated_at ?? null);
  const [states, setStates] = useState<Record<string, RollSubmitState>>({});
  const [results, setResults] = useState<Record<string, Result>>({});
  const confirmed = useRef<Record<string, Result>>({});
  const sending = useRef(new Set<string>());
  const state = states[sessionId] ?? null;
  const restore = keptRoll.pending && !sending.current.has(sessionId) ? recoverRegisterCommit<SessionAttendance>(keptRoll.pending.key, async (saved) => {
    onSubmitted();
    return recorded([registerText.rollRecordedForThisSession], saved.roll_updated_at ?? undefined);
  }) : null;
  const result: Result = results[sessionId] ?? (restore?.state === "unknown" ? { ...restore, check: { ...restore.check, run: async () => keepResult(await restore.check.run(), setResult) } } : { state: "none" });
  const setResult = (value: Result) => {
    if (value.state === "confirmed" || value.state === "refused") keptRoll.setPending(null);
    if (value.state === "confirmed") confirmed.current[sessionId] = value;
    setResults((held) => ({ ...held, [sessionId]: value }));
  };
  const onOpenChange = (_id: string, open: boolean) => {
    if (open && result.state === "confirmed") setResult({ state: "none" });
    else if (!open && !state?.changedCount && result.state === "none") {
      const saved = confirmed.current[sessionId];
      if (saved) setResult(saved);
    }
  };
  const receiveState = useCallback(
    (next: RollSubmitState) => {
      setStates((held) =>
        held[sessionId] === next ? held : { ...held, [sessionId]: next },
      );
    },
    [sessionId],
  );
  if (!roll) return { box: null, onOpenChange };
  const id = `roll-${sessionId}`;
  const blockers = (): Blocker[] => {
    if (roll.status === "cancelled")
      return [
        {
          kind: "missing",
          part: "roll",
          sentence: registerText.thisSessionIsCancelledItsRollCannotBeRecorded,
        },
      ];
    if (!state?.markedCount)
      return [
        {
          kind: "missing",
          part: "roll",
          sentence: registerText.markAtLeastOneLearnerBeforeRecordingTheRoll,
        },
      ];
    if (state.held && !state.changedCount)
      return [{ kind: "unchanged", part: "roll" }];
    if (!state.canSubmit)
      return [
        {
          kind: "conflict",
          part: "roll",
          sentence: registerText.resolveTheNewerRollBeforeRecordingTheseMarks,
        },
      ];
    return [];
  };
  const box: TaskBox<SessionAttendance> = {
    kind: "task",
    id,
    job: registerText.recordThisSessionSRoll,
    label: registerText.roll,
    owner: { system: "imam" },
    fold: "choice",
    read: { status: "ready", value: roll, readAt: new Date().toISOString() },
    detail: () => state?.summary ?? registerText.readTheMarksBeforeRecordingTheRoll,
    state: () => ({
      class: result.state === "confirmed" ? "done" : state?.changedCount
        ? "draft"
        : roll.status === "held"
          ? "done"
          : "waiting",
      word: result.state === "confirmed" ? registerText.rollRecorded : state?.changedCount
        ? registerText.changedNotRecorded
        : roll.status === "held"
          ? registerText.rollRecorded
          : registerText.rollNotRecorded,
    }),
    exits: result.state === "confirmed" || (state?.held && !state.changedCount)
      ? [{ label: registerText.updateRoll, reopens: true }] : undefined,
    act: result.state === "unknown" ? { does: "check", verb: result.check.verb } : {
      does: "open",
      verb: roll.status === "held" ? registerText.updateRoll : registerText.takeRoll,
    },
    candidate: {
      eligible: roll.status !== "cancelled",
      rank: null,
      blocked: roll.status === "cancelled" ? blockers()[0] ?? null : null,
    },
    draft: result.state !== "confirmed" && Boolean(state?.changedCount),
    result,
    parts: [
      {
        id: "roll",
        label: registerText.marks,
        state: { class: "draft", word: registerText.notRecorded },
        markedByBox: true,
        owner: { system: "imam" },
        fold: "never",
        draft: "browser",
        keep: keptRoll,
        render: () => (
          <>
          {result.state !== "confirmed" && confirmed.current[sessionId] && <p>{registerText.rollRecordedForThisSession}</p>}
          <RollRegister
            key={roll.session_id}
            roll={roll}
            rowsOnly
            showHeading={false}
            recoveryUntil="roll-recorded"
            foot="none"
            foldSettled={false}
            participation={false}
            controlsRef={controls}
            onSubmitStateChange={receiveState}
            onSubmitted={() => onSubmitted()}
          />
          </>
        ),
      },
    ],
    commit: {
      verb: roll.status === "held" ? registerText.updateRoll : registerText.recordRoll,
      cap: registerText.whenYouRecordTheRoll,
      effects: () => state?.held && state.changedMarks?.length ? state.changedMarks : [
        {
          id: "marks",
          sentence:
            state?.summary ?? registerText.recordTheMarkedLearnersForThisSession,
        },
      ],
      blockers,
      run: async (key) => {
        sending.current.add(sessionId);
        try {
        keptRoll.setPending({ key });
        const next = await registerCommit(
          key,
          async () => {
            const outcome = await controls.current!.submitOutcome(key);
            if (outcome.kind === "recorded" || outcome.kind === "adopted") {
              const saved = outcome.roll;
              const markedCount = saved.roster.filter((row) => row.status).length;
              setStates((held) => ({ ...held, [sessionId]: {
                canSubmit: false, submitting: false, markedCount,
                unmarkedCount: saved.roster.length - markedCount,
                changedCount: 0, held: saved.status === "held",
                summary: registerPhrases.rollRecorded(markedCount, saved.roster.length),
              } }));
              return outcome.roll;
            }
            if (outcome.kind === "refused")
              throw new ApiError(422, outcome.reason);
            if (outcome.kind === "conflict")
              throw new ApiError(
                409,
                registerText.aNewerRollIsAvailableYourMarksAreKeptChoose,
              );
            throw new Error(registerText.notConfirmed);
          },
          (saved) => recorded([registerText.rollRecordedForThisSession], saved.roll_updated_at ?? undefined),
        );
        return keepResult(next, setResult);
        } finally { sending.current.delete(sessionId); }
      },
    },
  };
  return { box, onOpenChange };
}
